import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { Button, Input, Label, Spinner } from "reactstrap";
import {
	createMonitoringWritingAssistantBrandGuidelines,
	getMonitoringWritingAssistantBrandGuidelines,
	updateMonitoringWritingAssistantBrandGuidelines,
} from "../../../services/monitoringApi";

const EMPTY_FORM = {
	voice: "",
	doList: [],
	dontList: [],
	examples: "",
};

function guidelinesField(guidelines, camel, snake) {
	return guidelines?.[camel] ?? guidelines?.[snake];
}

function guidelinesId(guidelines) {
	if (guidelines == null) return null;
	const id = guidelines.id ?? guidelines.brand_guidelines_id ?? guidelines.brandGuidelinesId;
	return id != null && id !== "" ? Number(id) : null;
}

function normalizeStringList(value) {
	if (!Array.isArray(value)) return [];
	return value.map((item) => String(item ?? "").trim()).filter(Boolean);
}

function guidelinesToForm(guidelines) {
	if (!guidelines) return { ...EMPTY_FORM, doList: [], dontList: [] };
	return {
		voice: guidelinesField(guidelines, "voice", "voice") || "",
		doList: normalizeStringList(
			guidelinesField(guidelines, "doList", "do_list")
		),
		dontList: normalizeStringList(
			guidelinesField(guidelines, "dontList", "dont_list")
		),
		examples: guidelinesField(guidelines, "examples", "examples") || "",
	};
}

function formsEqual(a, b) {
	return (
		a.voice === b.voice &&
		a.examples === b.examples &&
		JSON.stringify(a.doList) === JSON.stringify(b.doList) &&
		JSON.stringify(a.dontList) === JSON.stringify(b.dontList)
	);
}

function GuidelineListField({ label, placeholder, items, onAdd, onRemove, disabled }) {
	const [draft, setDraft] = useState("");

	function handleAdd() {
		const value = draft.trim();
		if (!value) return;
		onAdd(value);
		setDraft("");
	}

	return (
		<div className="mb-0 monitoring__writing-guidelines-list-field">
			<Label className="monitoring__writing-guidelines-field-label">{label}</Label>
			<div className="monitoring__writing-guidelines-list-row">
				<Input
					value={draft}
					onChange={(event) => setDraft(event.target.value)}
					placeholder={placeholder}
					disabled={disabled}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							handleAdd();
						}
					}}
				/>
				<Button
					type="button"
					color="secondary"
					outline
					onClick={handleAdd}
					disabled={disabled || !draft.trim()}
					aria-label={`Add ${label.toLowerCase()} item`}
				>
					<Plus size={14} aria-hidden />
				</Button>
			</div>
			{items.length === 0 ? (
				<p className="monitoring__writing-guidelines-empty-hint mb-0 mt-2">No items yet.</p>
			) : (
				<ul className="monitoring__writing-guidelines-chip-list mb-0 mt-2">
					{items.map((item) => (
						<li key={item} className="monitoring__writing-guidelines-chip">
							<span>{item}</span>
							<button
								type="button"
								className="monitoring__writing-guidelines-chip-remove"
								onClick={() => onRemove(item)}
								disabled={disabled}
								aria-label={`Remove ${item}`}
							>
								<X size={12} aria-hidden />
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

export default function WritingAssistantBrandGuidelinesPanel({ websiteId }) {
	const [form, setForm] = useState(EMPTY_FORM);
	const [baseline, setBaseline] = useState(EMPTY_FORM);
	const [brandGuidelinesIdState, setBrandGuidelinesIdState] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState("");
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [saved, setSaved] = useState(false);

	const loadGuidelines = useCallback(async () => {
		setLoading(true);
		setLoadError("");
		const res = await getMonitoringWritingAssistantBrandGuidelines(websiteId);
		setLoading(false);
		if (!res.ok) {
			setLoadError(res.message || "Could not load brand guidelines.");
			setBrandGuidelinesIdState(null);
			setForm(EMPTY_FORM);
			setBaseline(EMPTY_FORM);
			return;
		}
		const nextForm = guidelinesToForm(res.guidelines);
		setForm(nextForm);
		setBaseline(nextForm);
		setBrandGuidelinesIdState(guidelinesId(res.guidelines));
	}, [websiteId]);

	useEffect(() => {
		loadGuidelines();
	}, [loadGuidelines]);

	const hasChanges = useMemo(() => !formsEqual(form, baseline), [form, baseline]);
	const isEmptyRecord = brandGuidelinesIdState == null;

	function setField(key, value) {
		setSaved(false);
		setSaveError("");
		setForm((previous) => ({ ...previous, [key]: value }));
	}

	function addDoItem(value) {
		setSaved(false);
		setSaveError("");
		setForm((previous) => ({
			...previous,
			doList: previous.doList.includes(value)
				? previous.doList
				: [...previous.doList, value],
		}));
	}

	function removeDoItem(value) {
		setSaved(false);
		setSaveError("");
		setForm((previous) => ({
			...previous,
			doList: previous.doList.filter((item) => item !== value),
		}));
	}

	function addDontItem(value) {
		setSaved(false);
		setSaveError("");
		setForm((previous) => ({
			...previous,
			dontList: previous.dontList.includes(value)
				? previous.dontList
				: [...previous.dontList, value],
		}));
	}

	function removeDontItem(value) {
		setSaved(false);
		setSaveError("");
		setForm((previous) => ({
			...previous,
			dontList: previous.dontList.filter((item) => item !== value),
		}));
	}

	async function handleSave() {
		if (saving || !hasChanges) return;
		setSaving(true);
		setSaveError("");
		setSaved(false);
		const payload = {
			voice: form.voice,
			doList: form.doList,
			dontList: form.dontList,
			examples: form.examples,
		};
		const res =
			brandGuidelinesIdState == null
				? await createMonitoringWritingAssistantBrandGuidelines(websiteId, payload)
				: await updateMonitoringWritingAssistantBrandGuidelines(
						websiteId,
						brandGuidelinesIdState,
						payload
					);
		setSaving(false);
		if (!res.ok) {
			setSaveError(res.message || "Could not save brand guidelines.");
			return;
		}
		const nextForm = guidelinesToForm(res.guidelines) || form;
		const nextId = guidelinesId(res.guidelines) ?? brandGuidelinesIdState;
		setForm(nextForm);
		setBaseline(nextForm);
		setBrandGuidelinesIdState(nextId);
		setSaved(true);
	}

	if (loading) {
		return (
			<div className="monitoring__writing-setup">
				<p className="monitoring__writing-setup-empty mb-0">Loading brand guidelines…</p>
			</div>
		);
	}

	return (
		<div className="monitoring__writing-setup">
			<div className="monitoring__writing-setup-head monitoring__writing-setup-head--stacked">
				<p className="monitoring__writing-setup-lead mb-0">
					Brand voice the AI Writing Assistant follows when drafting for this website.
				</p>
			</div>

			{isEmptyRecord ? (
				<p className="monitoring__writing-guidelines-notice mb-3">
					No brand guidelines exist yet for this website - saving below will create the
					first record.
				</p>
			) : null}

			{loadError ? <p className="text-danger small mb-3">{loadError}</p> : null}

			<div className="monitoring__writing-setup-card monitoring__writing-guidelines-card">
				<h3 className="monitoring__writing-guidelines-title">Guidelines</h3>

				<div className="monitoring__writing-guidelines-form">
					<div className="monitoring__writing-guidelines-field monitoring__writing-guidelines-field--full">
						<Label for="writing-guidelines-voice" className="monitoring__writing-guidelines-field-label">
							Voice
						</Label>
						<Input
							id="writing-guidelines-voice"
							type="textarea"
							rows={4}
							value={form.voice}
							onChange={(event) => setField("voice", event.target.value)}
							placeholder="Describe the brand's tone of voice…"
							disabled={saving}
						/>
					</div>

					<div className="monitoring__writing-guidelines-lists">
						<GuidelineListField
							label="Do list"
							placeholder="Add a do list item…"
							items={form.doList}
							onAdd={addDoItem}
							onRemove={removeDoItem}
							disabled={saving}
						/>

						<GuidelineListField
							label="Don't list"
							placeholder={"Add a don't list item…"}
							items={form.dontList}
							onAdd={addDontItem}
							onRemove={removeDontItem}
							disabled={saving}
						/>
					</div>

					<div className="monitoring__writing-guidelines-field monitoring__writing-guidelines-field--full">
						<Label
							for="writing-guidelines-examples"
							className="monitoring__writing-guidelines-field-label"
						>
							Examples
						</Label>
						<Input
							id="writing-guidelines-examples"
							type="textarea"
							rows={4}
							value={form.examples}
							onChange={(event) => setField("examples", event.target.value)}
							placeholder="Example copy that reflects the desired voice…"
							disabled={saving}
						/>
					</div>
				</div>

				<div className="monitoring__writing-guidelines-footer">
					<div className="monitoring__writing-guidelines-actions">
						<Button
							color="primary"
							onClick={handleSave}
							disabled={saving || !hasChanges}
						>
							{saving ? <Spinner size="sm" className="me-2" /> : null}
							Save
						</Button>
						{saved ? <span className="text-success small">Saved.</span> : null}
						{saveError ? <span className="text-danger small">{saveError}</span> : null}
						{!hasChanges && !saved ? (
							<span className="monitoring__writing-guidelines-save-hint small text-muted">
								Make a change to enable save.
							</span>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
}
